import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  Utensils, Package, Heart, GraduationCap, ArrowRight,
  MapPin, Users, Globe, ImageIcon, DollarSign
} from 'lucide-react';
import axios from 'axios';
import { useSiteContent } from '../hooks/useSiteContent';
import FoodBucketSection from './components/FoodBucketSection';
import HygieneKitSection from './components/HygieneKitSection';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const CostBreakdownTable = ({ data }) => {
  if (!data || !data.items || data.items.length === 0) return null;
  return (
    <div className="bg-white border border-[#E5E7EB] rounded-lg overflow-hidden" data-testid={`breakdown-${data.title?.replace(/\s/g, '-').toLowerCase()}`}>
      <div className="bg-[#0A0A0A] px-5 py-3">
        <h4 className="font-heading text-sm font-bold text-white">{data.title}</h4>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-xs min-w-[400px]">
          <thead className="bg-[#F8F9FA] border-b border-[#E5E7EB]">
            <tr>
              <th className="text-left py-2.5 px-4 font-semibold text-[#4B5563]">Item</th>
              <th className="text-center py-2.5 px-3 font-semibold text-[#4B5563]">Qty</th>
              <th className="text-right py-2.5 px-3 font-semibold text-[#4B5563]">Cost</th>
              <th className="text-right py-2.5 px-4 font-semibold text-[#4B5563]">Total</th>
            </tr>
          </thead>
          <tbody>
            {data.items.map((item, i) => (
              <tr key={i} className="border-b border-[#F1F5F9] last:border-0">
                <td className="py-2.5 px-4 font-medium text-[#0A0A0A]">{item.name}</td>
                <td className="py-2.5 px-3 text-center text-[#4B5563]">{item.quantity}</td>
                <td className="py-2.5 px-3 text-right text-[#4B5563]">${parseFloat(item.cost_per_unit || 0).toFixed(2)}</td>
                <td className="py-2.5 px-4 text-right font-semibold text-[#0A0A0A]">${parseFloat(item.total || 0).toFixed(2)}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="bg-[#FEF2F2] border-t-2 border-[#BA1826]/20">
              <td colSpan="3" className="py-3 px-4 font-heading font-bold text-sm text-[#0A0A0A]">{data.total_label || 'Total'}</td>
              <td className="py-3 px-4 text-right font-heading font-bold text-sm text-[#BA1826]">${parseFloat(data.total || 0).toFixed(2)}</td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
};

const Projects = () => {
  const [costData, setCostData] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);

  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/public/site-content/local_cost_breakdown`)
      .then(res => {
        setCostData(res.data?.data);
        setLastUpdated(res.data?.updated_at);
      })
      .catch(() => {});
  }, []);

  const { data: cmsPrograms } = useSiteContent('local_programs_cards', {
    food_assistance: { impact: 'Our goal: support 50+ families each month', supporting: 'Providing consistent food support to families across New York City.' },
    hygiene_essentials: { impact: 'Our goal: support 50+ families each month', supporting: 'Ensuring individuals and families have access to essential hygiene products.' },
    emergency_aid: { impact: 'Providing rapid support within 24\u201348 hours', supporting: 'Help us respond when families need it most.' },
    cpr_training: { impact: 'Training 50+ community members each year', supporting: 'Building safer communities through life-saving skills.' },
  });

  const localPrograms = [
    {
      title: 'Food Assistance Program',
      location: 'New York City',
      status: 'Ongoing',
      description: 'Providing consistent food support to families facing food insecurity across New York City.',
      impact: cmsPrograms.food_assistance?.impact || 'Our goal: support 50+ families each month',
      supporting: cmsPrograms.food_assistance?.supporting || 'Providing consistent food support to families across New York City.',
      icon: Utensils,
    },
    {
      title: 'Hygiene Essentials Distribution',
      location: 'New York City',
      status: 'Ongoing',
      description: 'Distributing toiletries, hygiene products, and personal care items to individuals and families in need across NYC.',
      impact: cmsPrograms.hygiene_essentials?.impact || 'Our goal: support 50+ families each month',
      supporting: cmsPrograms.hygiene_essentials?.supporting || 'Ensuring individuals and families have access to essential hygiene products.',
      icon: Package,
    },
    {
      title: 'Emergency Aid Response',
      location: 'New York Area',
      status: 'Active',
      description: 'Providing rapid-response support for individuals and families facing unexpected hardships. This includes emergency food delivery, hygiene essentials, and basic support during crisis situations, with response times typically within 24\u201348 hours.',
      impact: cmsPrograms.emergency_aid?.impact || 'Providing rapid support within 24\u201348 hours',
      supporting: cmsPrograms.emergency_aid?.supporting || 'Help us respond when families need it most.',
      icon: Heart,
    },
    {
      title: 'Community CPR Training',
      location: 'New York City',
      status: 'Quarterly',
      description: 'Providing free CPR and AED training to community members, equipping individuals with life-saving skills. We are expanding our efforts to support local and recreational sports leagues through CPR training and AED machine donations, helping create safer environments for athletes and the communities they serve.',
      impact: cmsPrograms.cpr_training?.impact || 'Training 50+ community members each year',
      supporting: cmsPrograms.cpr_training?.supporting || 'Building safer communities through life-saving skills.',
      icon: GraduationCap,
    },
  ];

  const showPhotos = costData?.show_photos !== false;

  const ProgramCard = ({ program, index, testIdPrefix }) => (
    <div 
      className="bg-white border border-[#E5E7EB] overflow-hidden card-hover"
      data-testid={`${testIdPrefix}-card-${index}`}
    >
      {showPhotos && (
        <div className="h-48 bg-[#F8F9FA] border-b border-dashed border-[#E2E8F0] flex flex-col items-center justify-center p-6" data-testid={`${testIdPrefix}-image-placeholder-${index}`}>
          <ImageIcon className="w-6 h-6 text-[#CBD5E1] mb-2" />
          <p className="text-xs font-medium text-[#94A3B8] text-center">Add local program photo</p>
          <p className="text-[10px] text-[#CBD5E1] text-center mt-1">Use food support, hygiene supplies, emergency aid, CPR training, or event setup photo</p>
        </div>
      )}
      <div className="p-6">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-10 h-10 bg-[#FEE2E2] rounded flex items-center justify-center flex-shrink-0">
            <program.icon className="w-5 h-5 text-[#BA1826]" />
          </div>
          <span className={`text-xs font-semibold px-2 py-1 rounded ${
            program.status === 'Ongoing' ? 'bg-green-100 text-green-700' :
            program.status === 'Active' ? 'bg-blue-100 text-blue-700' :
            'bg-yellow-100 text-yellow-700'
          }`}>
            {program.status}
          </span>
        </div>
        <h3 className="font-heading text-lg font-bold text-[#0A0A0A] mb-1">
          {program.title}
        </h3>
        <div className="flex items-center gap-1.5 text-xs text-[#4B5563] mb-3">
          <MapPin className="w-3.5 h-3.5" />
          {program.location}
        </div>
        <p className="text-sm text-[#4B5563] mb-3 leading-relaxed">
          {program.description}
        </p>
        <div className="flex items-center gap-2 text-base font-bold text-[#BA1826] mb-2" data-testid={`${testIdPrefix}-impact-${index}`}>
          <Users className="w-4 h-4" />
          {program.impact}
        </div>
        <p className="text-xs text-[#4B5563] italic">
          {program.supporting}
        </p>
      </div>
    </div>
  );

  const foodTotal = costData?.food_bucket?.total || 70.73;
  const hygieneTotal = costData?.hygiene_kit?.total || 28.95;
  const combinedTotal = foodTotal + hygieneTotal;

  return (
    <div>
      {/* Header */}
      <section className="bg-[#0A0A0A] py-12" data-testid="programs-hero">
        <div className="px-6 text-center">
          <p className="text-xs uppercase tracking-[0.2em] text-[#BA1826] font-semibold mb-2">
            What We Do
          </p>
          <h1 className="font-heading text-3xl sm:text-4xl font-bold text-white mb-3" data-testid="programs-title">
            Our Programs
          </h1>
          <p className="text-gray-400 text-sm max-w-md mx-auto">
            Delivering consistent, hands-on support to families across New York City and beyond.
          </p>
        </div>
      </section>

      {/* Local Programs — New York */}
      <section className="py-10 bg-white" data-testid="local-programs">
        <div className="px-6">
          <div className="text-center mb-8">
            <div className="inline-flex items-center gap-2 bg-[#FEF2F2] border border-[#FECACA] rounded-full px-4 py-1.5 mb-3">
              <MapPin className="w-3.5 h-3.5 text-[#BA1826]" />
              <span className="text-xs font-semibold text-[#BA1826]">New York City</span>
            </div>
            <h2 className="font-heading text-2xl sm:text-3xl font-bold text-[#0A0A0A] mb-2">
              Local Programs
            </h2>
            <p className="text-sm text-[#4B5563] max-w-lg mx-auto">
              Our NYC-based initiatives provide direct, hands-on support to families and individuals in need across the five boroughs.
            </p>
          </div>

          {/* New flyer-style Food Bucket + Hygiene Kit sections */}
          <div className="space-y-8 mb-10">
            <FoodBucketSection
              costBreakdown={costData?.food_bucket && costData?.visible !== false ? (
                <div className="bg-white rounded-b-lg p-5 md:p-8" data-testid="food-transparency-breakdown">
                  <div className="text-center mb-4">
                    <p className="text-[10px] uppercase tracking-[0.2em] text-[#BA1826] font-semibold">Transparency Breakdown</p>
                    <p className="text-xs text-[#4B5563]">Exactly where every dollar of a Food Bucket goes.</p>
                  </div>
                  <CostBreakdownTable data={costData.food_bucket} />
                </div>
              ) : null}
            />

            <HygieneKitSection
              costBreakdown={costData?.hygiene_kit && costData?.visible !== false ? (
                <div className="bg-white rounded-b-lg p-5 md:p-8" data-testid="hygiene-transparency-breakdown">
                  <div className="text-center mb-4">
                    <p className="text-[10px] uppercase tracking-[0.2em] text-[#BA1826] font-semibold">Transparency Breakdown</p>
                    <p className="text-xs text-[#4B5563]">Exactly where every dollar of a Hygiene Kit goes.</p>
                  </div>
                  <CostBreakdownTable data={costData.hygiene_kit} />
                </div>
              ) : null}
            />
          </div>

          {/* Remaining local programs (Emergency Aid + CPR) */}
          <div className="grid md:grid-cols-2 gap-6">
            {localPrograms.slice(2).map((program, index) => (
              <ProgramCard key={program.title} program={program} index={index + 2} testIdPrefix="local" />
            ))}
          </div>
        </div>
      </section>

      {/* Combined Family Support Summary (moved below both flyer sections) */}
      {costData?.visible !== false && (
        <section className="py-10 bg-[#F8F9FA]" data-testid="family-support-section">
          <div className="px-6 max-w-3xl mx-auto">
            <div className="bg-[#0A0A0A] rounded-xl p-6 text-center" data-testid="family-support-summary">
              <p className="text-xs uppercase tracking-[0.2em] text-[#BA1826] font-semibold mb-2">Full Family Support</p>
              <div className="flex items-center justify-center gap-3 md:gap-4 flex-wrap mb-4">
                <div className="bg-white/10 rounded-lg px-4 md:px-5 py-3">
                  <p className="text-[10px] md:text-xs text-gray-400">Food Bucket</p>
                  <p className="font-heading text-base md:text-lg font-bold text-white">${foodTotal.toFixed(2)}</p>
                </div>
                <span className="text-xl md:text-2xl text-gray-500 font-light">+</span>
                <div className="bg-white/10 rounded-lg px-4 md:px-5 py-3">
                  <p className="text-[10px] md:text-xs text-gray-400">Hygiene Kit</p>
                  <p className="font-heading text-base md:text-lg font-bold text-white">${hygieneTotal.toFixed(2)}</p>
                </div>
                <span className="text-xl md:text-2xl text-gray-500 font-light">=</span>
                <div className="bg-[#BA1826]/20 border border-[#BA1826]/40 rounded-lg px-4 md:px-5 py-3">
                  <p className="text-[10px] md:text-xs text-[#BA1826]">1 Family Supported</p>
                  <p className="font-heading text-base md:text-lg font-bold text-white">${combinedTotal.toFixed(2)}</p>
                </div>
              </div>
              <p className="text-xs text-gray-400 max-w-md mx-auto">
                Each family receives a complete food bucket and hygiene essentials kit, delivered with dignity and transparency.
              </p>
            </div>

            {lastUpdated && (
              <p className="text-[10px] text-[#9CA3AF] text-center mt-4" data-testid="last-updated">
                Last updated: {new Date(lastUpdated).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
              </p>
            )}
          </div>
        </section>
      )}

      {/* International Programs */}
      <section className="py-10 bg-white" data-testid="international-programs">
        <div className="px-6">
          <div className="text-center mb-8">
            <div className="inline-flex items-center gap-2 bg-[#FEF2F2] border border-[#FECACA] rounded-full px-4 py-1.5 mb-3">
              <Globe className="w-3.5 h-3.5 text-[#BA1826]" />
              <span className="text-xs font-semibold text-[#BA1826]">6 Countries</span>
            </div>
            <h2 className="font-heading text-2xl sm:text-3xl font-bold text-[#0A0A0A] mb-2">
              International Programs
            </h2>
            <p className="text-sm text-[#4B5563] max-w-lg mx-auto">
              We are working to provide monthly food, hygiene, and clean water support to families across Haiti, Dominican Republic, Colombia, El Salvador, Ecuador, and Jamaica through our standardized support model.
            </p>
          </div>

          <div className="bg-white border border-[#E5E7EB] rounded-lg p-6 max-w-xl mx-auto text-center">
            <div className="grid grid-cols-3 gap-4 mb-5">
              <div>
                <p className="text-2xl font-bold text-[#BA1826]">6</p>
                <p className="text-xs text-[#4B5563]">Countries</p>
              </div>
              <div>
                <p className="text-2xl font-bold text-[#BA1826]">300</p>
                <p className="text-xs text-[#4B5563]">Target Families</p>
              </div>
              <div>
                <p className="text-2xl font-bold text-[#BA1826]">$125</p>
                <p className="text-xs text-[#4B5563]">Full Family Support</p>
              </div>
            </div>
            <p className="text-sm text-[#4B5563] mb-4">
              Each family receives food essentials, hygiene products, and a clean water system every month through school-based distribution.
            </p>
            <Link 
              to="/international" 
              className="btn-primary text-sm inline-flex"
              data-testid="view-international-btn"
            >
              View All Country Programs <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-10 bg-[#BA1826]" data-testid="programs-cta">
        <div className="px-6 text-center">
          <h2 className="font-heading text-2xl sm:text-3xl font-bold text-white mb-3">
            Help Us Reach More Families
          </h2>
          <p className="text-white/80 max-w-md mx-auto mb-6 text-sm">
            Your support makes these programs possible. Together, we can reach more communities and change more lives.
          </p>
          <div className="flex flex-wrap justify-center gap-3">
            <Link to="/donate" className="bg-white text-[#BA1826] px-6 py-2.5 font-semibold rounded hover:bg-gray-100 transition-all text-sm" data-testid="programs-donate-btn">
              Support Our Work
            </Link>
            <Link to="/involved" className="bg-transparent text-white border-2 border-white px-6 py-2.5 font-semibold rounded hover:bg-white/10 transition-all text-sm" data-testid="programs-volunteer-btn">
              Volunteer With Us
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Projects;
